// Multi-language translations for the POS system

export type Language = 'en' | 'ur' | 'ar' | 'zh' | 'hi' | 'sd' | 'bal' | 'ps';

export interface LanguageOption {
  code: Language;
  name: string;
  nativeName: string;
  dir: 'ltr' | 'rtl';
}

export const languages: LanguageOption[] = [
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', dir: 'rtl' },
  { code: 'sd', name: 'Sindhi', nativeName: 'سنڌي', dir: 'rtl' },
  { code: 'ps', name: 'Pashto', nativeName: 'پښتو', dir: 'rtl' },
  { code: 'bal', name: 'Balochi', nativeName: 'بلوچی', dir: 'rtl' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', dir: 'ltr' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', dir: 'ltr' },
];

type TranslationKeys = {
  // Common
  save: string;
  cancel: string;
  delete: string;
  edit: string;
  add: string;
  search: string;
  filter: string;
  loading: string;
  noData: string;
  actions: string;
  status: string;
  active: string;
  inactive: string;
  yes: string;
  no: string;
  confirm: string;
  back: string;
  next: string;
  submit: string;
  close: string;
  
  // Navigation
  dashboard: string;
  pos: string;
  products: string;
  customers: string;
  invoices: string;
  reports: string;
  staff: string;
  settings: string;
  activityLog: string;
  users: string;
  
  // POS
  pointOfSale: string;
  cart: string;
  emptyCart: string;
  addToCart: string;
  checkout: string;
  subtotal: string;
  discount: string;
  tax: string;
  total: string;
  payment: string;
  cash: string;
  card: string;
  change: string;
  print: string;
  newSale: string;
  holdSale: string;
  scanBarcode: string;
  quantity: string;
  price: string;
  amount: string;
  
  // Customers
  customer: string;
  customerName: string;
  phone: string;
  email: string;
  address: string;
  walkInCustomer: string;
  selectCustomer: string;
  addCustomer: string;
  
  // Products
  product: string;
  productName: string;
  category: string;
  sku: string;
  barcode: string;
  costPrice: string;
  salePrice: string;
  stock: string;
  lowStock: string;
  outOfStock: string;
  inStock: string;
  
  // Orders
  orderType: string;
  dineIn: string;
  takeaway: string;
  delivery: string;
  deliveryCharge: string;
  serviceCharge: string;
  
  // Invoices
  invoice: string;
  invoiceNumber: string;
  date: string;
  paid: string;
  unpaid: string;
  partial: string;
  printInvoice: string;
  thermalReceipt: string;
  a4Invoice: string;
  
  // Reports
  salesReport: string;
  profitLoss: string;
  inventory: string;
  topProducts: string;
  todaySales: string;
  weeklySales: string;
  monthlySales: string;
  
  // Settings
  businessInfo: string;
  taxSettings: string;
  receiptSettings: string;
  appearance: string;
  language: string;
  selectLanguage: string;
  
  // Staff
  staffManagement: string;
  attendance: string;
  salary: string;
  expenses: string;
  
  // Auth
  login: string;
  logout: string;
  signUp: string;
  forgotPassword: string;
  resetPassword: string;
  
  // Messages
  successSaved: string;
  successDeleted: string;
  errorOccurred: string;
  confirmDelete: string;
  thankYouPurchase: string;
};

const translations: Record<Language, TranslationKeys> = {
  en: {
    // Common
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    filter: 'Filter',
    loading: 'Loading...',
    noData: 'No data available',
    actions: 'Actions',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    yes: 'Yes',
    no: 'No',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    submit: 'Submit',
    close: 'Close',
    
    // Navigation
    dashboard: 'Dashboard',
    pos: 'POS',
    products: 'Products',
    customers: 'Customers',
    invoices: 'Invoices',
    reports: 'Reports',
    staff: 'Staff',
    settings: 'Settings',
    activityLog: 'Activity Log',
    users: 'Users',
    
    // POS
    pointOfSale: 'Point of Sale',
    cart: 'Cart',
    emptyCart: 'Cart is empty',
    addToCart: 'Add to Cart',
    checkout: 'Checkout',
    subtotal: 'Subtotal',
    discount: 'Discount',
    tax: 'Tax',
    total: 'Total',
    payment: 'Payment',
    cash: 'Cash',
    card: 'Card',
    change: 'Change',
    print: 'Print',
    newSale: 'New Sale',
    holdSale: 'Hold Sale',
    scanBarcode: 'Scan Barcode',
    quantity: 'Quantity',
    price: 'Price',
    amount: 'Amount',
    
    // Customers
    customer: 'Customer',
    customerName: 'Customer Name',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    walkInCustomer: 'Walk-in Customer',
    selectCustomer: 'Select Customer',
    addCustomer: 'Add Customer',
    
    // Products
    product: 'Product',
    productName: 'Product Name',
    category: 'Category',
    sku: 'SKU',
    barcode: 'Barcode',
    costPrice: 'Cost Price',
    salePrice: 'Sale Price',
    stock: 'Stock',
    lowStock: 'Low Stock',
    outOfStock: 'Out of Stock',
    inStock: 'In Stock',
    
    // Orders
    orderType: 'Order Type',
    dineIn: 'Dine-In',
    takeaway: 'Takeaway',
    delivery: 'Delivery',
    deliveryCharge: 'Delivery Charge',
    serviceCharge: 'Service Charge',
    
    // Invoices
    invoice: 'Invoice',
    invoiceNumber: 'Invoice #',
    date: 'Date',
    paid: 'Paid',
    unpaid: 'Unpaid',
    partial: 'Partial',
    printInvoice: 'Print Invoice',
    thermalReceipt: 'Thermal Receipt',
    a4Invoice: 'A4 Invoice',
    
    // Reports
    salesReport: 'Sales Report',
    profitLoss: 'Profit & Loss',
    inventory: 'Inventory',
    topProducts: 'Top Products',
    todaySales: 'Today\'s Sales',
    weeklySales: 'This Week',
    monthlySales: 'This Month',
    
    // Settings
    businessInfo: 'Business Info',
    taxSettings: 'Tax Settings',
    receiptSettings: 'Receipt Settings',
    appearance: 'Appearance',
    language: 'Language',
    selectLanguage: 'Select Language',
    
    // Staff
    staffManagement: 'Staff Management',
    attendance: 'Attendance',
    salary: 'Salary',
    expenses: 'Expenses',
    
    // Auth
    login: 'Login',
    logout: 'Logout',
    signUp: 'Sign Up',
    forgotPassword: 'Forgot Password?',
    resetPassword: 'Reset Password',
    
    // Messages
    successSaved: 'Saved successfully',
    successDeleted: 'Deleted successfully',
    errorOccurred: 'An error occurred',
    confirmDelete: 'Are you sure you want to delete?',
    thankYouPurchase: 'Thank you for your purchase!',
  },
  
  ur: {
    // Common
    save: 'محفوظ کریں',
    cancel: 'منسوخ کریں',
    delete: 'حذف کریں',
    edit: 'ترمیم کریں',
    add: 'شامل کریں',
    search: 'تلاش کریں',
    filter: 'فلٹر',
    loading: 'لوڈ ہو رہا ہے...',
    noData: 'کوئی ڈیٹا دستیاب نہیں',
    actions: 'اعمال',
    status: 'حیثیت',
    active: 'فعال',
    inactive: 'غیر فعال',
    yes: 'ہاں',
    no: 'نہیں',
    confirm: 'تصدیق کریں',
    back: 'واپس',
    next: 'اگلا',
    submit: 'جمع کریں',
    close: 'بند کریں',
    
    // Navigation
    dashboard: 'ڈیش بورڈ',
    pos: 'پی او ایس',
    products: 'مصنوعات',
    customers: 'گاہک',
    invoices: 'انوائسز',
    reports: 'رپورٹس',
    staff: 'عملہ',
    settings: 'ترتیبات',
    activityLog: 'سرگرمی لاگ',
    users: 'صارفین',
    
    // POS
    pointOfSale: 'پوائنٹ آف سیل',
    cart: 'کارٹ',
    emptyCart: 'کارٹ خالی ہے',
    addToCart: 'کارٹ میں شامل کریں',
    checkout: 'چیک آؤٹ',
    subtotal: 'ذیلی کل',
    discount: 'رعایت',
    tax: 'ٹیکس',
    total: 'کل',
    payment: 'ادائیگی',
    cash: 'نقد',
    card: 'کارڈ',
    change: 'واپسی',
    print: 'پرنٹ',
    newSale: 'نئی فروخت',
    holdSale: 'ہولڈ کریں',
    scanBarcode: 'بارکوڈ اسکین کریں',
    quantity: 'مقدار',
    price: 'قیمت',
    amount: 'رقم',
    
    // Customers
    customer: 'گاہک',
    customerName: 'گاہک کا نام',
    phone: 'فون',
    email: 'ای میل',
    address: 'پتہ',
    walkInCustomer: 'واک ان گاہک',
    selectCustomer: 'گاہک منتخب کریں',
    addCustomer: 'گاہک شامل کریں',
    
    // Products
    product: 'پروڈکٹ',
    productName: 'پروڈکٹ کا نام',
    category: 'زمرہ',
    sku: 'ایس کے یو',
    barcode: 'بارکوڈ',
    costPrice: 'لاگت کی قیمت',
    salePrice: 'فروخت کی قیمت',
    stock: 'اسٹاک',
    lowStock: 'کم اسٹاک',
    outOfStock: 'اسٹاک ختم',
    inStock: 'دستیاب',
    
    // Orders
    orderType: 'آرڈر کی قسم',
    dineIn: 'ڈائن ان',
    takeaway: 'ٹیک اوے',
    delivery: 'ڈیلیوری',
    deliveryCharge: 'ڈیلیوری چارج',
    serviceCharge: 'سروس چارج',
    
    // Invoices
    invoice: 'انوائس',
    invoiceNumber: 'انوائس نمبر',
    date: 'تاریخ',
    paid: 'ادا شدہ',
    unpaid: 'غیر ادا شدہ',
    partial: 'جزوی',
    printInvoice: 'انوائس پرنٹ کریں',
    thermalReceipt: 'تھرمل رسید',
    a4Invoice: 'A4 انوائس',
    
    // Reports
    salesReport: 'فروخت کی رپورٹ',
    profitLoss: 'نفع و نقصان',
    inventory: 'انوینٹری',
    topProducts: 'ٹاپ پروڈکٹس',
    todaySales: 'آج کی فروخت',
    weeklySales: 'اس ہفتے',
    monthlySales: 'اس ماہ',
    
    // Settings
    businessInfo: 'کاروباری معلومات',
    taxSettings: 'ٹیکس ترتیبات',
    receiptSettings: 'رسید ترتیبات',
    appearance: 'ظاہری شکل',
    language: 'زبان',
    selectLanguage: 'زبان منتخب کریں',
    
    // Staff
    staffManagement: 'عملے کا انتظام',
    attendance: 'حاضری',
    salary: 'تنخواہ',
    expenses: 'اخراجات',
    
    // Auth
    login: 'لاگ ان',
    logout: 'لاگ آؤٹ',
    signUp: 'سائن اپ',
    forgotPassword: 'پاس ورڈ بھول گئے؟',
    resetPassword: 'پاس ورڈ ری سیٹ کریں',
    
    // Messages
    successSaved: 'کامیابی سے محفوظ ہوگیا',
    successDeleted: 'کامیابی سے حذف ہوگیا',
    errorOccurred: 'ایک خرابی پیش آئی',
    confirmDelete: 'کیا آپ واقعی حذف کرنا چاہتے ہیں؟',
    thankYouPurchase: 'خریداری کا شکریہ!',
  },
  
  ar: {
    // Common
    save: 'حفظ',
    cancel: 'إلغاء',
    delete: 'حذف',
    edit: 'تعديل',
    add: 'إضافة',
    search: 'بحث',
    filter: 'تصفية',
    loading: 'جاري التحميل...',
    noData: 'لا توجد بيانات',
    actions: 'إجراءات',
    status: 'الحالة',
    active: 'نشط',
    inactive: 'غير نشط',
    yes: 'نعم',
    no: 'لا',
    confirm: 'تأكيد',
    back: 'رجوع',
    next: 'التالي',
    submit: 'إرسال',
    close: 'إغلاق',
    
    // Navigation
    dashboard: 'لوحة التحكم',
    pos: 'نقطة البيع',
    products: 'المنتجات',
    customers: 'العملاء',
    invoices: 'الفواتير',
    reports: 'التقارير',
    staff: 'الموظفين',
    settings: 'الإعدادات',
    activityLog: 'سجل النشاط',
    users: 'المستخدمين',
    
    // POS
    pointOfSale: 'نقطة البيع',
    cart: 'السلة',
    emptyCart: 'السلة فارغة',
    addToCart: 'أضف للسلة',
    checkout: 'الدفع',
    subtotal: 'المجموع الفرعي',
    discount: 'خصم',
    tax: 'ضريبة',
    total: 'الإجمالي',
    payment: 'الدفع',
    cash: 'نقداً',
    card: 'بطاقة',
    change: 'الباقي',
    print: 'طباعة',
    newSale: 'بيع جديد',
    holdSale: 'تعليق البيع',
    scanBarcode: 'مسح الباركود',
    quantity: 'الكمية',
    price: 'السعر',
    amount: 'المبلغ',
    
    // Customers
    customer: 'عميل',
    customerName: 'اسم العميل',
    phone: 'الهاتف',
    email: 'البريد الإلكتروني',
    address: 'العنوان',
    walkInCustomer: 'عميل عابر',
    selectCustomer: 'اختر عميل',
    addCustomer: 'إضافة عميل',
    
    // Products
    product: 'منتج',
    productName: 'اسم المنتج',
    category: 'الفئة',
    sku: 'رمز المنتج',
    barcode: 'الباركود',
    costPrice: 'سعر التكلفة',
    salePrice: 'سعر البيع',
    stock: 'المخزون',
    lowStock: 'مخزون منخفض',
    outOfStock: 'نفذ المخزون',
    inStock: 'متوفر',
    
    // Orders
    orderType: 'نوع الطلب',
    dineIn: 'في المطعم',
    takeaway: 'سفري',
    delivery: 'توصيل',
    deliveryCharge: 'رسوم التوصيل',
    serviceCharge: 'رسوم الخدمة',
    
    // Invoices
    invoice: 'فاتورة',
    invoiceNumber: 'رقم الفاتورة',
    date: 'التاريخ',
    paid: 'مدفوع',
    unpaid: 'غير مدفوع',
    partial: 'جزئي',
    printInvoice: 'طباعة الفاتورة',
    thermalReceipt: 'إيصال حراري',
    a4Invoice: 'فاتورة A4',
    
    // Reports
    salesReport: 'تقرير المبيعات',
    profitLoss: 'الربح والخسارة',
    inventory: 'المخزون',
    topProducts: 'أفضل المنتجات',
    todaySales: 'مبيعات اليوم',
    weeklySales: 'هذا الأسبوع',
    monthlySales: 'هذا الشهر',
    
    // Settings
    businessInfo: 'معلومات العمل',
    taxSettings: 'إعدادات الضرائب',
    receiptSettings: 'إعدادات الإيصال',
    appearance: 'المظهر',
    language: 'اللغة',
    selectLanguage: 'اختر اللغة',
    
    // Staff
    staffManagement: 'إدارة الموظفين',
    attendance: 'الحضور',
    salary: 'الراتب',
    expenses: 'المصروفات',
    
    // Auth
    login: 'تسجيل الدخول',
    logout: 'تسجيل الخروج',
    signUp: 'إنشاء حساب',
    forgotPassword: 'نسيت كلمة المرور؟',
    resetPassword: 'إعادة تعيين كلمة المرور',
    
    // Messages
    successSaved: 'تم الحفظ بنجاح',
    successDeleted: 'تم الحذف بنجاح',
    errorOccurred: 'حدث خطأ',
    confirmDelete: 'هل أنت متأكد من الحذف؟',
    thankYouPurchase: 'شكراً لشرائك!',
  },
  
  zh: {
    // Common
    save: '保存',
    cancel: '取消',
    delete: '删除',
    edit: '编辑',
    add: '添加',
    search: '搜索',
    filter: '筛选',
    loading: '加载中...',
    noData: '暂无数据',
    actions: '操作',
    status: '状态',
    active: '活跃',
    inactive: '未激活',
    yes: '是',
    no: '否',
    confirm: '确认',
    back: '返回',
    next: '下一步',
    submit: '提交',
    close: '关闭',
    
    // Navigation
    dashboard: '仪表板',
    pos: '收银台',
    products: '产品',
    customers: '客户',
    invoices: '发票',
    reports: '报表',
    staff: '员工',
    settings: '设置',
    activityLog: '活动日志',
    users: '用户',
    
    // POS
    pointOfSale: '销售点',
    cart: '购物车',
    emptyCart: '购物车为空',
    addToCart: '加入购物车',
    checkout: '结账',
    subtotal: '小计',
    discount: '折扣',
    tax: '税费',
    total: '总计',
    payment: '付款',
    cash: '现金',
    card: '刷卡',
    change: '找零',
    print: '打印',
    newSale: '新订单',
    holdSale: '挂单',
    scanBarcode: '扫描条码',
    quantity: '数量',
    price: '单价',
    amount: '金额',
    
    // Customers
    customer: '客户',
    customerName: '客户姓名',
    phone: '电话',
    email: '邮箱',
    address: '地址',
    walkInCustomer: '散客',
    selectCustomer: '选择客户',
    addCustomer: '添加客户',
    
    // Products
    product: '产品',
    productName: '产品名称',
    category: '类别',
    sku: 'SKU',
    barcode: '条形码',
    costPrice: '成本价',
    salePrice: '销售价',
    stock: '库存',
    lowStock: '库存不足',
    outOfStock: '缺货',
    inStock: '有货',
    
    // Orders
    orderType: '订单类型',
    dineIn: '堂食',
    takeaway: '外带',
    delivery: '外卖',
    deliveryCharge: '配送费',
    serviceCharge: '服务费',
    
    // Invoices
    invoice: '发票',
    invoiceNumber: '发票号',
    date: '日期',
    paid: '已付',
    unpaid: '未付',
    partial: '部分付款',
    printInvoice: '打印发票',
    thermalReceipt: '热敏小票',
    a4Invoice: 'A4发票',
    
    // Reports
    salesReport: '销售报表',
    profitLoss: '损益表',
    inventory: '库存',
    topProducts: '热销产品',
    todaySales: '今日销售',
    weeklySales: '本周',
    monthlySales: '本月',
    
    // Settings
    businessInfo: '商家信息',
    taxSettings: '税务设置',
    receiptSettings: '小票设置',
    appearance: '外观',
    language: '语言',
    selectLanguage: '选择语言',
    
    // Staff
    staffManagement: '员工管理',
    attendance: '考勤',
    salary: '工资',
    expenses: '支出',
    
    // Auth
    login: '登录',
    logout: '退出',
    signUp: '注册',
    forgotPassword: '忘记密码？',
    resetPassword: '重置密码',
    
    // Messages
    successSaved: '保存成功',
    successDeleted: '删除成功',
    errorOccurred: '发生错误',
    confirmDelete: '确定删除吗？',
    thankYouPurchase: '感谢您的购买！',
  },
  
  hi: {
    // Common
    save: 'सेव करें',
    cancel: 'रद्द करें',
    delete: 'हटाएं',
    edit: 'संपादित करें',
    add: 'जोड़ें',
    search: 'खोजें',
    filter: 'फ़िल्टर',
    loading: 'लोड हो रहा है...',
    noData: 'कोई डेटा उपलब्ध नहीं',
    actions: 'कार्रवाई',
    status: 'स्थिति',
    active: 'सक्रिय',
    inactive: 'निष्क्रिय',
    yes: 'हां',
    no: 'नहीं',
    confirm: 'पुष्टि करें',
    back: 'वापस',
    next: 'अगला',
    submit: 'जमा करें',
    close: 'बंद करें',
    
    // Navigation
    dashboard: 'डैशबोर्ड',
    pos: 'पीओएस',
    products: 'उत्पाद',
    customers: 'ग्राहक',
    invoices: 'इनवॉयस',
    reports: 'रिपोर्ट',
    staff: 'स्टाफ',
    settings: 'सेटिंग्स',
    activityLog: 'गतिविधि लॉग',
    users: 'उपयोगकर्ता',
    
    // POS
    pointOfSale: 'पॉइंट ऑफ सेल',
    cart: 'कार्ट',
    emptyCart: 'कार्ट खाली है',
    addToCart: 'कार्ट में जोड़ें',
    checkout: 'चेकआउट',
    subtotal: 'उप-योग',
    discount: 'छूट',
    tax: 'कर',
    total: 'कुल',
    payment: 'भुगतान',
    cash: 'नकद',
    card: 'कार्ड',
    change: 'बाकी',
    print: 'प्रिंट',
    newSale: 'नई बिक्री',
    holdSale: 'होल्ड करें',
    scanBarcode: 'बारकोड स्कैन करें',
    quantity: 'मात्रा',
    price: 'कीमत',
    amount: 'राशि',
    
    // Customers
    customer: 'ग्राहक',
    customerName: 'ग्राहक का नाम',
    phone: 'फोन',
    email: 'ईमेल',
    address: 'पता',
    walkInCustomer: 'वॉक-इन ग्राहक',
    selectCustomer: 'ग्राहक चुनें',
    addCustomer: 'ग्राहक जोड़ें',
    
    // Products
    product: 'उत्पाद',
    productName: 'उत्पाद का नाम',
    category: 'श्रेणी',
    sku: 'एसकेयू',
    barcode: 'बारकोड',
    costPrice: 'लागत मूल्य',
    salePrice: 'बिक्री मूल्य',
    stock: 'स्टॉक',
    lowStock: 'कम स्टॉक',
    outOfStock: 'स्टॉक में नहीं',
    inStock: 'उपलब्ध',
    
    // Orders
    orderType: 'ऑर्डर प्रकार',
    dineIn: 'डाइन-इन',
    takeaway: 'टेकअवे',
    delivery: 'डिलीवरी',
    deliveryCharge: 'डिलीवरी चार्ज',
    serviceCharge: 'सर्विस चार्ज',
    
    // Invoices
    invoice: 'इनवॉयस',
    invoiceNumber: 'इनवॉयस नंबर',
    date: 'दिनांक',
    paid: 'भुगतान हो गया',
    unpaid: 'अवैतनिक',
    partial: 'आंशिक',
    printInvoice: 'इनवॉयस प्रिंट करें',
    thermalReceipt: 'थर्मल रसीद',
    a4Invoice: 'A4 इनवॉयस',
    
    // Reports
    salesReport: 'बिक्री रिपोर्ट',
    profitLoss: 'लाभ और हानि',
    inventory: 'इन्वेंटरी',
    topProducts: 'टॉप उत्पाद',
    todaySales: 'आज की बिक्री',
    weeklySales: 'इस सप्ताह',
    monthlySales: 'इस महीने',
    
    // Settings
    businessInfo: 'व्यापार जानकारी',
    taxSettings: 'कर सेटिंग्स',
    receiptSettings: 'रसीद सेटिंग्स',
    appearance: 'दिखावट',
    language: 'भाषा',
    selectLanguage: 'भाषा चुनें',
    
    // Staff
    staffManagement: 'स्टाफ प्रबंधन',
    attendance: 'उपस्थिति',
    salary: 'वेतन',
    expenses: 'खर्च',
    
    // Auth
    login: 'लॉगिन',
    logout: 'लॉगआउट',
    signUp: 'साइन अप',
    forgotPassword: 'पासवर्ड भूल गए?',
    resetPassword: 'पासवर्ड रीसेट करें',
    
    // Messages
    successSaved: 'सफलतापूर्वक सहेजा गया',
    successDeleted: 'सफलतापूर्वक हटाया गया',
    errorOccurred: 'एक त्रुटि हुई',
    confirmDelete: 'क्या आप वाकई हटाना चाहते हैं?',
    thankYouPurchase: 'खरीदारी के लिए धन्यवाद!',
  },
  
  sd: {
    // Common - Sindhi
    save: 'محفوظ ڪريو',
    cancel: 'منسوخ ڪريو',
    delete: 'ختم ڪريو',
    edit: 'ترميم ڪريو',
    add: 'شامل ڪريو',
    search: 'ڳولا ڪريو',
    filter: 'فلٽر',
    loading: 'لوڊ ٿي رهيو آهي...',
    noData: 'ڪا ڊيٽا ناهي',
    actions: 'عمل',
    status: 'حيثيت',
    active: 'فعال',
    inactive: 'غير فعال',
    yes: 'ها',
    no: 'نه',
    confirm: 'تصديق ڪريو',
    back: 'واپس',
    next: 'اڳيان',
    submit: 'جمع ڪريو',
    close: 'بند ڪريو',
    dashboard: 'ڊيش بورڊ',
    pos: 'پي او ايس',
    products: 'شيون',
    customers: 'گراهڪ',
    invoices: 'انوائسز',
    reports: 'رپورٽون',
    staff: 'عملو',
    settings: 'سيٽنگز',
    activityLog: 'سرگرمي لاگ',
    users: 'يوزرز',
    pointOfSale: 'پوائنٽ آف سيل',
    cart: 'ڪارٽ',
    emptyCart: 'ڪارٽ خالي آهي',
    addToCart: 'ڪارٽ ۾ شامل ڪريو',
    checkout: 'چيڪ آئوٽ',
    subtotal: 'ذيلي ڪل',
    discount: 'رعايت',
    tax: 'ٽيڪس',
    total: 'ڪل',
    payment: 'ادائگي',
    cash: 'نقد',
    card: 'ڪارڊ',
    change: 'واپسي',
    print: 'پرنٽ',
    newSale: 'نئين وڪري',
    holdSale: 'هولڊ ڪريو',
    scanBarcode: 'بارڪوڊ اسڪين ڪريو',
    quantity: 'مقدار',
    price: 'قيمت',
    amount: 'رقم',
    customer: 'گراهڪ',
    customerName: 'گراهڪ جو نالو',
    phone: 'فون',
    email: 'اي ميل',
    address: 'پتو',
    walkInCustomer: 'واڪ ان گراهڪ',
    selectCustomer: 'گراهڪ چونڊيو',
    addCustomer: 'گراهڪ شامل ڪريو',
    product: 'پراڊڪٽ',
    productName: 'پراڊڪٽ جو نالو',
    category: 'درجو',
    sku: 'ايس ڪي يو',
    barcode: 'بارڪوڊ',
    costPrice: 'لاڳت قيمت',
    salePrice: 'وڪري قيمت',
    stock: 'اسٽاڪ',
    lowStock: 'گهٽ اسٽاڪ',
    outOfStock: 'اسٽاڪ ختم',
    inStock: 'موجود',
    orderType: 'آرڊر جو قسم',
    dineIn: 'ڊائن ان',
    takeaway: 'ٽيڪ اوي',
    delivery: 'ڊليوري',
    deliveryCharge: 'ڊليوري چارج',
    serviceCharge: 'سروس چارج',
    invoice: 'انوائس',
    invoiceNumber: 'انوائس نمبر',
    date: 'تاريخ',
    paid: 'ادا ٿيل',
    unpaid: 'غير ادا ٿيل',
    partial: 'جزوي',
    printInvoice: 'انوائس پرنٽ ڪريو',
    thermalReceipt: 'ٿرمل رسيد',
    a4Invoice: 'A4 انوائس',
    salesReport: 'وڪري رپورٽ',
    profitLoss: 'نفعو ۽ نقصان',
    inventory: 'انوينٽري',
    topProducts: 'ٽاپ پراڊڪٽس',
    todaySales: 'اڄ جي وڪري',
    weeklySales: 'هن هفتي',
    monthlySales: 'هن مهيني',
    businessInfo: 'ڪاروباري معلومات',
    taxSettings: 'ٽيڪس سيٽنگز',
    receiptSettings: 'رسيد سيٽنگز',
    appearance: 'ظاهري شڪل',
    language: 'ٻولي',
    selectLanguage: 'ٻولي چونڊيو',
    staffManagement: 'عملي جو انتظام',
    attendance: 'حاضري',
    salary: 'تنخواه',
    expenses: 'خرچا',
    login: 'لاگ ان',
    logout: 'لاگ آئوٽ',
    signUp: 'سائن اپ',
    forgotPassword: 'پاسورڊ وساري ويو؟',
    resetPassword: 'پاسورڊ ري سيٽ ڪريو',
    successSaved: 'ڪاميابي سان محفوظ ٿيو',
    successDeleted: 'ڪاميابي سان ختم ٿيو',
    errorOccurred: 'هڪ نقص پيش آيو',
    confirmDelete: 'ڇا توهان يقينن ختم ڪرڻ چاهيو ٿا؟',
    thankYouPurchase: 'خريداري جي مهرباني!',
  },
  
  ps: {
    // Common - Pashto
    save: 'خوندي کړئ',
    cancel: 'لغوه کړئ',
    delete: 'ړنګول',
    edit: 'سمول',
    add: 'اضافه کړئ',
    search: 'لټون',
    filter: 'فلټر',
    loading: 'بار کیږي...',
    noData: 'معلومات نشته',
    actions: 'کړنې',
    status: 'حالت',
    active: 'فعال',
    inactive: 'غیر فعال',
    yes: 'هو',
    no: 'نه',
    confirm: 'تایید',
    back: 'شاته',
    next: 'بل',
    submit: 'سپارل',
    close: 'بندول',
    dashboard: 'ډشبورډ',
    pos: 'پي او ایس',
    products: 'محصولات',
    customers: 'پیرودونکي',
    invoices: 'بیلونه',
    reports: 'راپورونه',
    staff: 'کارمندان',
    settings: 'ترتیبات',
    activityLog: 'فعالیت لاګ',
    users: 'کاروونکي',
    pointOfSale: 'پوائنټ آف سیل',
    cart: 'کارټ',
    emptyCart: 'کارټ خالي دی',
    addToCart: 'کارټ ته اضافه کړئ',
    checkout: 'چک آوټ',
    subtotal: 'فرعي مجموعه',
    discount: 'تخفیف',
    tax: 'مالیه',
    total: 'ټول',
    payment: 'تادیه',
    cash: 'نغدي',
    card: 'کارډ',
    change: 'پاتې',
    print: 'چاپ',
    newSale: 'نوی پلور',
    holdSale: 'ساتل',
    scanBarcode: 'بارکوډ سکین کړئ',
    quantity: 'مقدار',
    price: 'قیمت',
    amount: 'اندازه',
    customer: 'پیرودونکی',
    customerName: 'د پیرودونکي نوم',
    phone: 'تلیفون',
    email: 'برېښنالیک',
    address: 'پته',
    walkInCustomer: 'واک ان پیرودونکی',
    selectCustomer: 'پیرودونکی وټاکئ',
    addCustomer: 'پیرودونکی اضافه کړئ',
    product: 'محصول',
    productName: 'د محصول نوم',
    category: 'کتګوري',
    sku: 'SKU',
    barcode: 'بارکوډ',
    costPrice: 'لګښت قیمت',
    salePrice: 'پلور قیمت',
    stock: 'ذخیره',
    lowStock: 'کم ذخیره',
    outOfStock: 'ذخیره پای ته رسېدلې',
    inStock: 'موجود',
    orderType: 'د امر ډول',
    dineIn: 'دننه خوراک',
    takeaway: 'ټیک اوی',
    delivery: 'لیږد',
    deliveryCharge: 'د لیږد فیس',
    serviceCharge: 'د خدمت فیس',
    invoice: 'بیل',
    invoiceNumber: 'د بیل شمیره',
    date: 'نیټه',
    paid: 'ورکړل شوی',
    unpaid: 'نه ورکړل شوی',
    partial: 'جزوي',
    printInvoice: 'بیل چاپ کړئ',
    thermalReceipt: 'تودوخه رسید',
    a4Invoice: 'A4 بیل',
    salesReport: 'د پلور راپور',
    profitLoss: 'ګټه او زیان',
    inventory: 'انوینټري',
    topProducts: 'غوره محصولات',
    todaySales: 'نن ورځ پلور',
    weeklySales: 'دا اونۍ',
    monthlySales: 'دا میاشت',
    businessInfo: 'سوداګریز معلومات',
    taxSettings: 'مالیاتي ترتیبات',
    receiptSettings: 'رسید ترتیبات',
    appearance: 'ظاهري بڼه',
    language: 'ژبه',
    selectLanguage: 'ژبه وټاکئ',
    staffManagement: 'کارمند مدیریت',
    attendance: 'حاضري',
    salary: 'معاش',
    expenses: 'لګښتونه',
    login: 'ننوتل',
    logout: 'وتل',
    signUp: 'ګډون',
    forgotPassword: 'پټنوم مو هېر شو؟',
    resetPassword: 'پټنوم بیا تنظیم کړئ',
    successSaved: 'په بریالیتوب سره خوندي شول',
    successDeleted: 'په بریالیتوب سره ړنګ شول',
    errorOccurred: 'یوه تېروتنه وشوه',
    confirmDelete: 'ایا تاسو ډاډه یاست چې غواړئ ړنګ کړئ؟',
    thankYouPurchase: 'ستاسو د پېرلو مننه!',
  },
  
  bal: {
    // Common - Balochi
    save: 'محفوظ کن',
    cancel: 'لغو کن',
    delete: 'پاک کن',
    edit: 'اصلاح کن',
    add: 'اضافہ کن',
    search: 'گردگ',
    filter: 'فلٹر',
    loading: 'لوڈ بیت...',
    noData: 'ڈیٹا نیست',
    actions: 'کار',
    status: 'حالت',
    active: 'فعال',
    inactive: 'غیر فعال',
    yes: 'ہا',
    no: 'نہ',
    confirm: 'تصدیق کن',
    back: 'پدا',
    next: 'دیگہ',
    submit: 'جمع کن',
    close: 'بند کن',
    dashboard: 'ڈیش بورڈ',
    pos: 'پی او ایس',
    products: 'چیزاں',
    customers: 'گراہک',
    invoices: 'بل',
    reports: 'رپورٹ',
    staff: 'کارندہ',
    settings: 'سیٹنگ',
    activityLog: 'کار لاگ',
    users: 'یوزر',
    pointOfSale: 'پوائنٹ آف سیل',
    cart: 'کارٹ',
    emptyCart: 'کارٹ خالی انت',
    addToCart: 'کارٹ ءَ اضافہ کن',
    checkout: 'چیک آؤٹ',
    subtotal: 'ذیلی ٹوٹل',
    discount: 'رعایت',
    tax: 'ٹیکس',
    total: 'ٹوٹل',
    payment: 'ادائیگی',
    cash: 'نقد',
    card: 'کارڈ',
    change: 'واپسی',
    print: 'پرنٹ',
    newSale: 'نوکین فروخت',
    holdSale: 'ہولڈ کن',
    scanBarcode: 'بارکوڈ اسکین کن',
    quantity: 'مقدار',
    price: 'قیمت',
    amount: 'رقم',
    customer: 'گراہک',
    customerName: 'گراہک ءِ نام',
    phone: 'فون',
    email: 'ای میل',
    address: 'پتہ',
    walkInCustomer: 'واک ان گراہک',
    selectCustomer: 'گراہک انتخاب کن',
    addCustomer: 'گراہک اضافہ کن',
    product: 'چیز',
    productName: 'چیز ءِ نام',
    category: 'کیٹگری',
    sku: 'ایس کے یو',
    barcode: 'بارکوڈ',
    costPrice: 'لاگت قیمت',
    salePrice: 'فروخت قیمت',
    stock: 'اسٹاک',
    lowStock: 'کم اسٹاک',
    outOfStock: 'اسٽاک ختم',
    inStock: 'موجود',
    orderType: 'آرڈر ءِ قسم',
    dineIn: 'ڈائن ان',
    takeaway: 'ٹیک اوے',
    delivery: 'ڈلیوری',
    deliveryCharge: 'ڈلیوری چارج',
    serviceCharge: 'سروس چارج',
    invoice: 'بل',
    invoiceNumber: 'بل نمبر',
    date: 'تاریخ',
    paid: 'ادا',
    unpaid: 'غیر ادا',
    partial: 'جزوی',
    printInvoice: 'بل پرنٹ کن',
    thermalReceipt: 'تھرمل رسید',
    a4Invoice: 'A4 بل',
    salesReport: 'فروخت رپورٹ',
    profitLoss: 'نفع و نقصان',
    inventory: 'انوینٹری',
    topProducts: 'ٹاپ چیزاں',
    todaySales: 'اے روچ ءِ فروخت',
    weeklySales: 'اے ہفتہ',
    monthlySales: 'اے ماہ',
    businessInfo: 'کاروبار معلومات',
    taxSettings: 'ٹیکس سیٹنگ',
    receiptSettings: 'رسید سیٹنگ',
    appearance: 'ظاہری شکل',
    language: 'زبان',
    selectLanguage: 'زبان انتخاب کن',
    staffManagement: 'کارندہ انتظام',
    attendance: 'حاضری',
    salary: 'تنخواہ',
    expenses: 'خرچ',
    login: 'لاگ ان',
    logout: 'لاگ آؤٹ',
    signUp: 'سائن اپ',
    forgotPassword: 'پاسورڈ پروش ات؟',
    resetPassword: 'پاسورڈ ری سیٹ کن',
    successSaved: 'کامیابی ءَ محفوظ بوت',
    successDeleted: 'کامیابی ءَ پاک بوت',
    errorOccurred: 'یک خطا پیش آت',
    confirmDelete: 'شما مطمئن ات کہ پاک کنگ لوٹ ات؟',
    thankYouPurchase: 'شما ءِ خریداری ءِ مننت!',
  },
};

export default translations;
